import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, 
  Activity, 
  Leaf, 
  Brain, 
  Heart, 
  Bone, 
  Wind, 
  Eye, 
  BrainCircuit,
  Sparkles,
  Pill,
  Thermometer,
  Droplets,
  Syringe,
  Microscope,
  TestTube,
  Beaker,
  Stethoscope,
  HeartPulse
} from 'lucide-react';
import { PsoriasisIcon } from './PsoriasisIcon';
import { RespiratoryIcon } from './RespiratoryIcon';
import { DigestiveIcon } from './DigestiveIcon';
import { BrainIcon } from './BrainIcon';

const TreatmentSection = () => {
  const [showAll, setShowAll] = useState(false);

  const treatments = [
    {
      id: 1,
      title: 'Knee Joint Pain',
      description: 'Natural treatment for knee joint pain using traditional therapies and herbal remedies.',
      icon: Bone,
      color: 'bg-blue-100'
    },
    {
      id: 2,
      title: 'Cervical Spondylitis',
      description: 'Holistic approach to treat cervical spondylitis with yoga and naturopathy.',
      icon: Brain,
      color: 'bg-green-100'
    },
    {
      id: 3,
      title: 'Disc Bulge',
      description: 'Non-surgical treatment for disc bulge using natural therapies.',
      icon: Bone,
      color: 'bg-amber-100'
    },
    {
      id: 4,
      title: 'Psoriasis',
      description: 'Natural treatment for psoriasis using herbal remedies and lifestyle modifications.',
      icon: Eye,
      color: 'bg-sky-100'
    },
    {
      id: 5,
      title: 'Asthma',
      description: 'Comprehensive treatment for asthma using breathing exercises and natural remedies.',
      icon: Wind,
      color: 'bg-orange-100'
    },
    {
      id: 6,
      title: 'Arthritis',
      description: 'Natural treatment for arthritis focusing on pain relief and joint health.',
      icon: Activity,
      color: 'bg-purple-100'
    },
    {
      id: 7,
      title: 'Anal Fissure',
      description: 'Natural healing for anal fissures using herbal treatments.',
      icon: Pill,
      color: 'bg-red-100'
    },
    {
      id: 8,
      title: 'Spondylosis',
      description: 'Holistic treatment for spondylosis combining yoga and naturopathy.',
      icon: Bone,
      color: 'bg-indigo-100'
    },
    {
      id: 9,
      title: 'Calcaneal Spur',
      description: 'Natural treatment for heel pain and calcaneal spur.',
      icon: Bone,
      color: 'bg-pink-100'
    },
    {
      id: 10,
      title: 'Diabetes',
      description: 'Comprehensive diabetes management through lifestyle and natural remedies.',
      icon: Leaf,
      color: 'bg-yellow-100'
    },
    {
      id: 11,
      title: 'Diabetic Wound',
      description: 'Natural healing for diabetic wounds using herbal treatments.',
      icon: Eye,
      color: 'bg-teal-100'
    },
    {
      id: 12,
      title: 'Dandruff',
      description: 'Natural treatment for dandruff using herbal remedies.',
      icon: Eye,
      color: 'bg-cyan-100'
    },
    {
      id: 13,
      title: 'Digestive Disorders',
      description: 'Holistic treatment for various digestive issues.',
      icon: Pill,
      color: 'bg-emerald-100'
    },
    {
      id: 14,
      title: 'Dyslipidemia',
      description: 'Natural management of cholesterol and lipid levels.',
      icon: Heart,
      color: 'bg-rose-100'
    },
    {
      id: 15,
      title: 'Eczema',
      description: 'Natural treatment for eczema using herbal remedies.',
      icon: Eye,
      color: 'bg-violet-100'
    },
    {
      id: 16,
      title: 'Female Disorders',
      description: 'Holistic treatment for various female health issues.',
      icon: Heart,
      color: 'bg-fuchsia-100'
    },
    {
      id: 17,
      title: 'Fungal Infection',
      description: 'Natural treatment for fungal infections.',
      icon: Eye,
      color: 'bg-lime-100'
    },
    {
      id: 18,
      title: 'Fistula',
      description: 'Natural treatment for anal fistula.',
      icon: Pill,
      color: 'bg-orange-100'
    },
    {
      id: 19,
      title: 'Gall Bladder Stones',
      description: 'Natural treatment for gall bladder stones.',
      icon: Pill,
      color: 'bg-amber-100'
    },
    {
      id: 20,
      title: 'Hair Fall',
      description: 'Natural treatment for hair fall and scalp issues.',
      icon: Eye,
      color: 'bg-yellow-100'
    },
    {
      id: 21,
      title: 'Hypertension',
      description: 'Natural management of high blood pressure.',
      icon: Heart,
      color: 'bg-red-100'
    },
    {
      id: 22,
      title: 'Hydrocele',
      description: 'Natural treatment for hydrocele.',
      icon: Pill,
      color: 'bg-blue-100'
    },
    {
      id: 23,
      title: 'Infertility',
      description: 'Holistic treatment for infertility issues.',
      icon: Heart,
      color: 'bg-pink-100'
    },
    {
      id: 24,
      title: 'Kidney Stones',
      description: 'Natural treatment for kidney stones.',
      icon: Pill,
      color: 'bg-purple-100'
    },
    {
      id: 25,
      title: 'Leucoderma',
      description: 'Natural treatment for leucoderma.',
      icon: Eye,
      color: 'bg-gray-100'
    },
    {
      id: 26,
      title: 'Lipoma',
      description: 'Natural treatment for lipoma.',
      icon: Eye,
      color: 'bg-slate-100'
    },
    {
      id: 27,
      title: 'Migraine',
      description: 'Natural treatment for migraine and headaches.',
      icon: Brain,
      color: 'bg-indigo-100'
    },
    {
      id: 28,
      title: 'Obesity',
      description: 'Natural weight management program.',
      icon: Activity,
      color: 'bg-green-100'
    },
    {
      id: 29,
      title: 'Other Skin Diseases',
      description: 'Treatment for various skin conditions.',
      icon: Eye,
      color: 'bg-teal-100'
    },
    {
      id: 30,
      title: 'Peripheral Neuritis',
      description: 'Natural treatment for peripheral neuritis.',
      icon: BrainCircuit,
      color: 'bg-cyan-100'
    },
    {
      id: 31,
      title: 'Pimples',
      description: 'Natural treatment for acne and pimples.',
      icon: Eye,
      color: 'bg-rose-100'
    },
    {
      id: 32,
      title: 'Parkinsonism',
      description: 'Natural support for Parkinson\'s symptoms.',
      icon: Brain,
      color: 'bg-violet-100'
    },
    {
      id: 33,
      title: 'Paralysis',
      description: 'Natural rehabilitation for paralysis.',
      icon: Brain,
      color: 'bg-fuchsia-100'
    },
    {
      id: 34,
      title: 'Piles',
      description: 'Natural treatment for piles and hemorrhoids.',
      icon: Pill,
      color: 'bg-lime-100'
    },
    {
      id: 35,
      title: 'Sinusitis',
      description: 'Natural treatment for sinusitis.',
      icon: Wind,
      color: 'bg-emerald-100'
    },
    {
      id: 36,
      title: 'Thyroid',
      description: 'Natural management of thyroid disorders.',
      icon: HeartPulse,
      color: 'bg-blue-100'
    },
    {
      id: 37,
      title: 'Trigeminal Neuralgia',
      description: 'Natural treatment for trigeminal neuralgia.',
      icon: Brain,
      color: 'bg-indigo-100'
    },
    {
      id: 38,
      title: 'Tonsillitis',
      description: 'Natural treatment for tonsillitis.',
      icon: Wind,
      color: 'bg-purple-100'
    },
    {
      id: 39,
      title: 'Ulcer',
      description: 'Natural treatment for various types of ulcers.',
      icon: Pill,
      color: 'bg-pink-100'
    },
    {
      id: 40,
      title: 'Varicose Veins',
      description: 'Natural treatment for varicose veins.',
      icon: Heart,
      color: 'bg-red-100'
    },
    {
      id: 41,
      title: 'Varicose Ulcer',
      description: 'Natural treatment for varicose ulcers.',
      icon: Eye,
      color: 'bg-orange-100'
    },
    {
      id: 42,
      title: 'PCOD',
      description: 'Natural treatment for PCOD.',
      icon: Heart,
      color: 'bg-yellow-100'
    },
    {
      id: 43,
      title: 'Acid Reflux',
      description: 'Natural treatment for acid reflux.',
      icon: Pill,
      color: 'bg-green-100'
    },
    {
      id: 44,
      title: 'Gastritis',
      description: 'Natural treatment for gastritis.',
      icon: Pill,
      color: 'bg-teal-100'
    },
    {
      id: 45,
      title: 'Heart Burn',
      description: 'Natural treatment for heartburn.',
      icon: Heart,
      color: 'bg-cyan-100'
    },
    {
      id: 46,
      title: 'Hormonal Imbalance',
      description: 'Natural treatment for hormonal imbalances.',
      icon: Heart,
      color: 'bg-blue-100'
    },
    {
      id: 47,
      title: 'Urticaria',
      description: 'Natural treatment for urticaria.',
      icon: Eye,
      color: 'bg-indigo-100'
    },
    {
      id: 48,
      title: 'Worm Infestation',
      description: 'Natural treatment for worm infestation.',
      icon: Pill,
      color: 'bg-purple-100'
    },
    {
      id: 49,
      title: 'Cellulitis',
      description: 'Natural treatment for cellulitis.',
      icon: Eye,
      color: 'bg-pink-100'
    },
    {
      id: 50,
      title: 'Constipation',
      description: 'Natural treatment for constipation.',
      icon: Pill,
      color: 'bg-red-100'
    },
    {
      id: 51,
      title: 'Fibroids',
      description: 'Natural treatment for fibroids.',
      icon: Heart,
      color: 'bg-orange-100'
    },
    {
      id: 52,
      title: 'White Discharge',
      description: 'Natural treatment for white discharge.',
      icon: Heart,
      color: 'bg-yellow-100'
    }
  ];

  return (
    <section id="treatments" className="py-16 md:py-24 bg-swastik-lightEarth/50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-swastik-green font-medium">Our Specialties</span>
          <h2 className="section-title mx-auto after:left-1/2 after:-translate-x-1/2">Effective Natural Treatments for Chronic Diseases</h2>
          <p className="section-subtitle">
            We combine ancient Ayurvedic wisdom with modern medical understanding to provide holistic healing for conditions that often have limited options in conventional medicine.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {treatments.slice(0, showAll ? treatments.length : 6).map((treatment) => {
            const Icon = treatment.icon;
            // Update the path generation to match route definitions
            const path = `/treatments/${treatment.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
            
            return (
              <div key={treatment.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className={`${treatment.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className="h-6 w-6 text-swastik-blue" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{treatment.title}</h3>
                  <p className="text-gray-600 mb-4">{treatment.description}</p>
                  <Link 
                    to={path}
                    className="inline-flex items-center text-swastik-blue hover:text-swastik-blue/80 font-medium"
                  >
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
        
        {treatments.length > 6 && (
          <div className="text-center mt-8">
            <Button
              onClick={() => setShowAll(!showAll)}
              variant="outline"
              className="text-swastik-blue border-swastik-blue hover:bg-swastik-blue hover:text-white"
            >
              {showAll ? 'Show Less' : 'View All Treatments'}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TreatmentSection;
